import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Factory, Cog, Package, Warehouse, Wrench, TestTube, Lightbulb } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Factory,
      title: 'Metal Fabrication & Welding',
      description: 'Expert structural welding and custom metal fabrication for the most demanding industrial applications.',
      link: '/services/metal-fabrication',
      features: ['Structural Steel Fabrication', 'TIG/MIG/Stick Welding', 'Custom Sheet Metal Work', 'Assembly Services'],
    },
    {
      icon: Package,
      title: 'Turnkey Manufacturing',
      description: 'Complete end-to-end manufacturing solutions from design and engineering through production and delivery.',
      link: '/services/turnkey-manufacturing',
      features: ['Design Consultation', 'Prototyping', 'Production Management', 'Quality Assurance'],
    },
    {
      icon: Cog,
      title: 'CNC Precision Machining',
      description: 'State-of-the-art CNC machining capabilities for tight tolerance precision components.',
      link: '/services/cnc-machining',
      features: ['Multi-Axis CNC Milling', 'CNC Turning', 'Wire EDM', 'Surface Grinding'],
    },
    {
      icon: Warehouse,
      title: 'Warehousing & Supply Chain',
      description: 'Comprehensive logistics, inventory management, and just-in-time delivery solutions.',
      link: '/services/warehousing',
      features: ['Inventory Management', 'Kitting & Assembly', 'JIT Delivery', 'Vendor Consolidation'],
    },
    {
      icon: Wrench,
      title: 'Repair & Refurbishment',
      description: 'Expert repair and refurbishment services to extend the life of your critical equipment.',
      link: '/services/repair',
      features: ['Equipment Repair', 'Component Refurbishment', 'Preventive Maintenance', 'Emergency Services'],
    },
    {
      icon: TestTube,
      title: 'Hydro Static Testing & NDE',
      description: 'Comprehensive testing and non-destructive examination services for quality assurance.',
      link: '/services/testing',
      features: ['Hydrostatic Testing', 'Ultrasonic Testing', 'Radiographic Testing', 'Dye Penetrant Testing'],
    },
    {
      icon: Lightbulb,
      title: 'New Product Development',
      description: 'From concept to reality - complete product development and prototyping services.',
      link: '/services/development',
      features: ['Design Engineering', 'Rapid Prototyping', 'Material Selection', 'Manufacturing Optimization'],
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1920&q=80"
            alt="Our Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-blue-900/85"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white heading-font mb-6" data-testid="services-heading">
            Our Services
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Comprehensive manufacturing solutions backed by decades of expertise and state-of-the-art equipment.
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl border-2 border-gray-100 p-8 card-hover" data-testid={`service-item-${index}`}>
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <service.icon className="w-8 h-8 text-blue-700" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 heading-font mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-center text-sm text-gray-600">
                      <span className="w-1.5 h-1.5 bg-blue-700 rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={service.link}
                  className="inline-flex items-center text-blue-700 hover:text-blue-800 font-semibold"
                  data-testid={`service-link-${index}`}
                >
                  Learn More
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-700 to-blue-900 text-white" data-testid="services-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold heading-font mb-6">Ready to Get Started?</h2>
          <p className="text-lg text-blue-100 mb-8">
            Contact us today to discuss your manufacturing needs and receive a detailed quote.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-700 rounded-lg hover:bg-gray-100 font-semibold transition-all shadow-lg"
            data-testid="services-cta-button"
          >
            Request a Quote
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;